import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AuthenticatedImage from "../../components/AuthenticatedImage";
import { constructionRequest } from "../../utils/constructionApi";
import Sidebar from "../../components/Sidebar";
import ConTaskUpdateList from "./tasks/ConTaskUpdateList";
import { isImage, isVideo } from "./tasks/ConImageList";

function formatScheduleDate(dateStr) {
  if (!dateStr) return 'N/A';
  const match = dateStr.match(/(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : 'N/A';
}

function isToday(dateStr) {
  if (!dateStr) return false;

  try {
    // 如果时间字符串没有时区标识，假设为UTC时间
    let adjustedDateStr = dateStr;
    if (!dateStr.includes('+') && !dateStr.includes('Z')) {
      adjustedDateStr = dateStr + 'Z';
    }

    const d = new Date(adjustedDateStr);

    // 检查日期是否有效
    if (isNaN(d.getTime())) {
      return false;
    }

    const now = new Date();

    // 正确的时区转换方式：使用Intl.DateTimeFormat获取多伦多时区的日期
    const torontoDateParts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Toronto',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(d);

    const torontoNowParts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Toronto',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(now);

    const torontoDate = torontoDateParts.find(part => part.type === 'year').value + '-' +
      torontoDateParts.find(part => part.type === 'month').value + '-' +
      torontoDateParts.find(part => part.type === 'day').value;

    const torontoToday = torontoNowParts.find(part => part.type === 'year').value + '-' +
      torontoNowParts.find(part => part.type === 'month').value + '-' +
      torontoNowParts.find(part => part.type === 'day').value;

    return torontoDate === torontoToday;
  } catch (error) {
    console.error('Error in isToday function:', error, 'dateStr:', dateStr);
    return false;
  }
}

function isAfter(date1, date2) {
  if (!date1 || !date2) return false;
  
  const getDateOnly = (dateStr) => {
    const match = dateStr.match(/(\d{4})-(\d{2})-(\d{2})/);
    return match ? `${match[1]}-${match[2]}-${match[3]}` : '';
  };
  
  const d1 = getDateOnly(date1);
  const d2 = getDateOnly(date2);
  
  return d1 > d2;
}

/** Latest today's image for Update column thumbnail; videos get a placeholder. */
function pickTodayPreviewMedia(task, isTodayFn) {
  const list = (task.images || []).filter((img) =>
    isTodayFn(img.upload_time || img.upload_date)
  );
  if (list.length === 0) return { kind: "empty" };

  const parseT = (s) => {
    if (!s) return 0;
    const adj = !s.includes("+") && !s.includes("Z") ? s + "Z" : s;
    const t = new Date(adj).getTime();
    return isNaN(t) ? 0 : t;
  };

  const sorted = [...list].sort(
    (a, b) =>
      parseT(b.upload_time || b.upload_date) -
      parseT(a.upload_time || a.upload_date)
  );

  for (const item of sorted) {
    const idx = task.images.findIndex((x) => x === item);
    if (idx < 0) continue;
    if (isImage(item)) return { kind: "image", img: item, index: idx };
    if (isVideo(item)) return { kind: "video", index: idx };
  }
  return { kind: "empty" };
}

const ConMonitor = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [userRole, setUserRole] = useState("");
  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updateModalTaskId, setUpdateModalTaskId] = useState(null);

  const fetchTasks = useCallback(async (projId, signal) => {
    setError(null);
    setLoading(true);
    setTasks([]);
    try {
      const response = await constructionRequest(`/api/tasks?projectId=${encodeURIComponent(projId)}`, { signal });
      if (!response.ok) throw new Error("Failed to fetch tasks");
      const data = await response.json();
      const formattedTasks = data.map((task) => ({
        _id: task._id,
        task_name: task.task_name,
        status: task.status || "pending",
        progress: task.progress || 0,
        inspection_status: task.inspection_status || "N/A",
        schedule_start: task.schedule_start,
        schedule_finish: task.schedule_finish,
        estimate_start: task.estimate_start,
        estimate_finish: task.estimate_finish,
        days_left: calculateDaysLeft(task.schedule_finish),
        notes: task.notes || [],
        images: task.images || []
      }))
      // 按 schedule_start 日期排序（升序）
      .sort((a, b) => {
        // 处理没有 schedule_start 的任务，将其排到最后
        if (!a.schedule_start && !b.schedule_start) return 0;
        if (!a.schedule_start) return 1;
        if (!b.schedule_start) return -1;

        const dateA = new Date(a.schedule_start);
        const dateB = new Date(b.schedule_start);
        const diff = dateA - dateB;

        // 如果日期相同，按任务名称排序
        if (diff === 0) {
          return a.task_name.localeCompare(b.task_name);
        }
        return diff;
      });
      if (!signal?.aborted) setTasks(formattedTasks);
    } catch (error) {
      if (!signal?.aborted) setError(error.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setUpdateModalTaskId(null);
    const role = localStorage.getItem("userRole");
    if (!role) {
      navigate("/");
    } else {
      setUserRole(role);
      if (projectId) {
        fetchTasks(projectId, controller.signal);
      }
    }
    return () => controller.abort();
  }, [navigate, projectId, fetchTasks]);

  const calculateDaysLeft = (finishDate) => {
    if (!finishDate) return null;
    const finish = new Date(finishDate);
    const today = new Date();
    finish.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const diffTime = finish - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const getImgUrl = (img, taskId, imageIndex, isThumbnail = true) => {
    // Always use same-origin Flask proxy — private S3 URLs return 403 in the browser.
    if (taskId && imageIndex !== undefined) {
      const thumbnailParam = isThumbnail ? "?thumbnail=true&width=200" : "";
      return `/api/image/construction/task/${taskId}/${imageIndex}${thumbnailParam}`;
    }
    return null;
  };

  return (
    <div className="flex h-screen">
      <Sidebar userRole={userRole} projectId={projectId} scenario="construction" />
      <div className="flex-1 bg-gray-100 p-6 ml-[60px]">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Project Schedule</h2>
        {error && <p role="alert" className="text-red-600 mb-4">{error}</p>}
        {loading && <p>Loading tasks...</p>}
        {!loading && !error && tasks.length === 0 && <p>No tasks available.</p>}
        <table className="w-full bg-white border border-gray-300 shadow-md rounded-lg table-fixed">
          <thead>
            <tr className="bg-gray-200 text-gray-700">
              <th className="p-3" style={{width: '10%'}}>Task</th>
              <th className="p-3" style={{width: '8%'}}>Status</th>
              <th className="p-3" style={{width: '8%'}}>Progress</th>
              <th className="p-3" style={{width: '10%'}}>Inspection</th>
              <th className="p-3" style={{width: '12%'}}>Schedule</th>
              <th className="p-3" style={{width: '12%'}}>Estimate</th>
              <th className="p-3" style={{width: '8%'}}>Days Left</th>
              <th className="p-3" style={{width: '32%'}}>Update</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((task) => {
              // Note (Today) logic
              const todayNotes = (task.notes || []).filter((n) => isToday(n.date));
              const hasHistoryNotes = (task.notes || []).length > 0;
              const todayImages = (task.images || []).filter((img) =>
                isToday(img.upload_time || img.upload_date)
              );
              const hasHistoryImages = (task.images || []).length > 0;
              const hasUpdateHistory = hasHistoryNotes || hasHistoryImages;
              const previewMedia = pickTodayPreviewMedia(task, isToday);
              const previewText = todayNotes.map((n) => n.text).join(" | ");
              return (
                <tr key={task._id} className="border-t border-gray-300">
                  <td className="p-3 text-xs">{task.task_name}</td>
                  <td className="p-3">
                    {(() => {
                      const status = (task.status || '').toLowerCase();
                      if (status === 'working') {
                        return <span className="bg-yellow-100 text-yellow-600 px-3 py-1 rounded-full text-xs font-semibold">Working</span>;
                      } else if (status === 'not ready') {
                        return <span className="bg-gray-100 text-gray-500 px-3 py-1 rounded-full text-xs font-semibold">Not Ready</span>;
                      } else if (status === 'waiting book') {
                        return <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-xs font-semibold">Waiting Book</span>;
                      } else if (status === 'booked') {
                        return <span className="bg-purple-100 text-purple-600 px-3 py-1 rounded-full text-xs font-semibold">Booked</span>;
                      } else if (status === 'complete') {
                        return <span className="bg-green-100 text-green-600 px-3 py-1 rounded-full text-xs font-semibold">Complete</span>;
                      } else {
                        return <span className="bg-gray-100 text-gray-400 px-3 py-1 rounded-full text-xs font-semibold">{task.status || 'N/A'}</span>;
                      }
                    })()}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2 min-w-[100px]">
                      <div className="relative w-full h-3 bg-gray-200 rounded-full">
                        <div
                          className="absolute left-0 top-0 h-3 bg-blue-500 rounded-full"
                          style={{ width: `${task.progress || 0}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-700 font-semibold min-w-[40px] text-right" style={{width:'40px'}}>
                        {task.progress || 0}%
                      </span>
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    {task.inspection_status && task.inspection_status !== 'N/A' ? (
                      <span className={
                        task.inspection_status === 'Passed'
                          ? 'bg-green-100 text-green-600 px-2 py-0.5 rounded text-xs font-semibold'
                          : (task.inspection_status === 'TBD' || task.inspection_status === 'Scheduled')
                          ? 'bg-yellow-100 text-yellow-600 px-2 py-0.5 rounded text-xs font-semibold'
                          : 'bg-gray-100 text-gray-500 px-2 py-0.5 rounded text-xs font-semibold'
                      }>
                        {task.inspection_status}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">N/A</span>
                    )}
                  </td>
                  <td className="p-3">
                    {task.schedule_start && task.schedule_finish ? (
                      <>
                        <span className="text-xs text-gray-500">
                          Start: {formatScheduleDate(task.schedule_start)}
                        </span><br />
                        <span className="text-xs text-gray-500">
                          Finish: {formatScheduleDate(task.schedule_finish)}
                        </span>
                      </>
                    ) : (
                      <span className="text-gray-400 text-xs">N/A</span>
                    )}
                  </td>
                  <td className="p-3">
                    {task.estimate_start && task.estimate_finish ? (
                      <>
                        <span className={
                          `text-xs px-2 py-0.5 rounded font-semibold ${isAfter(task.estimate_start, task.schedule_start) ? 'bg-yellow-100 text-yellow-600' : 'text-gray-700'}`
                        }>
                          Start: {formatScheduleDate(task.estimate_start)}
                        </span><br />
                        <span className={
                          `text-xs px-2 py-0.5 rounded font-semibold ${isAfter(task.estimate_finish, task.schedule_finish) ? 'bg-red-100 text-red-500' : 'text-gray-500'}`
                        }>
                          Finish: {formatScheduleDate(task.estimate_finish)}
                        </span>
                      </>
                    ) : (
                      <span className="text-gray-400 text-xs">Start: N/A<br />Finish: N/A</span>
                    )}
                  </td>
                  <td className="p-3 text-center">
                    {(() => {
                      const status = (task.status || '').toLowerCase();
                      if (status === 'complete') {
                        return <span></span>;
                      } else if (task.days_left === 0) {
                        return <span className="bg-red-100 text-red-500 px-3 py-1 rounded-full text-xs font-semibold">0 days</span>;
                      } else if (task.days_left !== null) {
                        return <span className={
                          task.days_left <= 3 ?
                            "bg-red-100 text-red-500 px-3 py-1 rounded-full text-xs font-semibold" :
                          task.days_left <= 7 ?
                            "bg-yellow-100 text-yellow-600 px-3 py-1 rounded-full text-xs font-semibold" :
                            "text-gray-500 text-xs"
                        }>
                          {`${task.days_left} days`}
                        </span>;
                      } else {
                        return <span className="text-gray-500 text-xs">-</span>;
                      }
                    })()}
                  </td>
                  <td
                    className="p-3 text-xs text-center"
                    style={{ maxWidth: "32%", overflow: "hidden", verticalAlign: "middle" }}
                  >
                    {hasUpdateHistory ? (
                      <button
                        type="button"
                        className="cursor-pointer inline-flex items-center justify-center gap-2 max-w-full mx-auto bg-transparent border-0 p-0"
                        onClick={() => setUpdateModalTaskId(task._id)}
                        title={
                          previewText ||
                          (todayImages.length ? "Today’s media" : "View history")
                        }
                      >
                        <span className="flex-shrink-0 w-8 h-8 rounded overflow-hidden bg-gray-100 flex items-center justify-center">
                          {previewMedia.kind === "image" ? (
                            <AuthenticatedImage
                              src={getImgUrl(
                                previewMedia.img,
                                task._id,
                                previewMedia.index,
                                true
                              )}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : previewMedia.kind === "video" ? (
                            <span className="text-[10px] text-indigo-600 font-semibold" aria-hidden>
                              ▶
                            </span>
                          ) : (
                            <span className="text-[10px] text-gray-400">—</span>
                          )}
                        </span>
                        <span
                          className={`inline-block max-w-[220px] truncate align-middle ${
                            previewText
                              ? "text-blue-600 underline"
                              : "text-gray-400 underline"
                          }`}
                        >
                          {previewText
                            ? previewText
                            : todayImages.length > 0
                              ? "Media"
                              : "Before"}
                        </span>
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {updateModalTaskId && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded shadow-lg max-w-4xl w-full relative max-h-[90vh] overflow-y-auto">
              <button
                type="button"
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 text-xl z-10"
                onClick={() => setUpdateModalTaskId(null)}
                aria-label="Close"
              >
                ×
              </button>
              <ConTaskUpdateList taskId={updateModalTaskId} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConMonitor;
